import { Bell, ChevronDown, LogOut, Search, ShieldCheck } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Input, cn } from '@pnl/ui';
import { useAuthStore } from '../auth/auth.store';

export function Topbar() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickAway(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const initials =
    user?.fullName
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() ??
    user?.email?.slice(0, 2).toUpperCase() ??
    '··';

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <header className="border-ink-200 flex h-16 shrink-0 items-center justify-between gap-4 border-b bg-white px-6">
      <div className="max-w-md flex-1">
        <Input
          placeholder="Buscar trader, plano, transação…"
          leadingIcon={<Search size={14} />}
          className="bg-ink-50/60"
        />
      </div>

      <div className="flex items-center gap-2">
        <Badge tone="success" dot>
          API ok
        </Badge>

        <Button variant="ghost" size="icon" aria-label="Notificações">
          <Bell size={16} />
        </Button>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            data-testid="topbar-user-menu"
            aria-label="Abrir menu do usuário"
            onClick={() => setMenuOpen((v) => !v)}
            className={cn(
              'border-ink-200 flex items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-colors',
              'hover:bg-ink-50',
              menuOpen && 'bg-ink-50',
            )}
          >
            <div className="bg-brand-500 text-2xs flex h-7 w-7 items-center justify-center rounded-full font-mono font-bold text-white">
              {initials}
            </div>
            <div className="flex flex-col">
              <span className="text-ink-900 text-xs font-semibold leading-none">
                {user?.fullName || user?.email || 'Admin'}
              </span>
              <span className="text-2xs text-ink-500 mt-0.5">{user?.roles[0] ?? '—'}</span>
            </div>
            <ChevronDown size={14} className="text-ink-400" />
          </button>

          {menuOpen && (
            <div className="animate-slide-down border-ink-200 shadow-lift absolute right-0 top-full z-20 mt-1 w-56 rounded-lg border bg-white py-1">
              <div className="border-ink-100 border-b px-3 py-2">
                <p className="text-2xs text-ink-500 font-semibold uppercase tracking-wider">
                  Sessão
                </p>
                <p className="text-ink-900 mt-1 truncate text-sm font-medium">
                  {user?.email || '—'}
                </p>
              </div>
              <button
                type="button"
                className="text-ink-700 hover:bg-ink-50 flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/2fa');
                }}
              >
                <ShieldCheck size={14} />
                Configurar 2FA
              </button>
              <button
                type="button"
                className="text-danger-500 hover:bg-danger-50 flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                onClick={handleLogout}
              >
                <LogOut size={14} />
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
